import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineAlert } from "@/components/admin/feedback";
import { Select } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { apiErrorMessage } from "@/lib/api";
import { useCustomersQuery, useProductsQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";

type ScopeMode = "all" | "byProduct" | "byCustomer";

export type LicenseScopeSelection = { product?: string; customer?: string };

function scopeMode(scope: { product_id?: string; customer_id?: string }): ScopeMode {
  if (scope.product_id) return "byProduct";
  if (scope.customer_id) return "byCustomer";
  return "all";
}

/** Segmented Product / Customer scope for the Licenses hub. Emits exactly one scope at a time. */
export function LicenseScopeSelector({ scope, onChange, className }: {
  scope: { product_id?: string; customer_id?: string };
  onChange: (next: LicenseScopeSelection) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  // A mode chosen before a record is picked has nothing in `scope` yet; it is keyed to
  // the scope it was chosen under so a parent-driven scope change discards it.
  const scopeKey = `${scope.product_id ?? ""}\u0000${scope.customer_id ?? ""}`;
  const [pending, setPending] = useState<{ mode: ScopeMode; key: string } | null>(null);
  const mode = pending?.key === scopeKey ? pending.mode : scopeMode(scope);

  const selectMode = (next: string) => {
    if (!next || next === mode) return;
    setPending({ mode: next as ScopeMode, key: scopeKey });
    if (next === "all") onChange({});
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <ToggleGroup
        type="single"
        value={mode}
        onValueChange={selectMode}
        aria-label={t("licenses.scope.label", "Scope Licenses")}
      >
        <ToggleGroupItem value="all">{t("licenses.scope.all", "All")}</ToggleGroupItem>
        <ToggleGroupItem value="byProduct">{t("licenses.scope.byProduct", "By Product")}</ToggleGroupItem>
        <ToggleGroupItem value="byCustomer">{t("licenses.scope.byCustomer", "By Customer")}</ToggleGroupItem>
      </ToggleGroup>
      {mode === "byProduct" && (
        <ProductPicker value={scope.product_id ?? ""} onPick={(id) => onChange({ product: id })} />
      )}
      {mode === "byCustomer" && (
        <CustomerPicker value={scope.customer_id ?? ""} onPick={(id) => onChange({ customer: id })} />
      )}
    </div>
  );
}

function ProductPicker({ value, onPick }: { value: string; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const query = useProductsQuery();
  return (
    <ScopePicker
      value={value}
      onPick={onPick}
      records={query.data?.products}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      label={t("licenses.scope.pickProduct", "Choose a Product")}
      errorTitle={t("licenses.scope.productsError", "Products failed to load")}
    />
  );
}

function CustomerPicker({ value, onPick }: { value: string; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const query = useCustomersQuery();
  return (
    <ScopePicker
      value={value}
      onPick={onPick}
      records={query.data?.customers}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      label={t("licenses.scope.pickCustomer", "Choose a Customer")}
      errorTitle={t("licenses.scope.customersError", "Customers failed to load")}
    />
  );
}

function ScopePicker({ value, onPick, records, isPending, error, onRetry, label, errorTitle }: {
  value: string;
  onPick: (id: string) => void;
  records: readonly { id: string; name: string }[] | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  label: string;
  errorTitle: string;
}) {
  const { t } = useTranslation();
  if (error) {
    return <InlineAlert title={errorTitle} message={apiErrorMessage(error)} onRetry={onRetry} />;
  }
  const loading = isPending || !records;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Select
        aria-label={label}
        className="w-64"
        disabled={loading}
        value={loading ? "" : value}
        placeholder={loading ? t("licenses.scope.loading", "Loading…") : label}
        options={loading ? [] : records.map((record) => ({ value: record.id, label: record.name }))}
        onValueChange={(next) => {
          if (next && next !== value) onPick(next);
        }}
      />
      {value && <span className="truncate font-mono text-xs text-muted">{value}</span>}
    </div>
  );
}
